/**
 * @file useCanvasOverlays.ts
 * @description Что конструктор кладёт поверх сайта на канве (phase19.16):
 * корзина с образцом — на экране «Корзина» и в «Виде корзины» (SB120 этап 4,
 * `useCanvasCart`), баннер cookie — в «Счётчиках и cookie» (Р3г, SB134,
 * `useCanvasConsent`). Только свой черновик: на «Сейчас на сайте» и у
 * снимка (дизайн, версия) — ничего. Один хук, чтобы канва звала одну
 * связку и один `onLoad`, а новое наложение было строкой здесь.
 */

import { useCallback, type RefObject } from "react";

import { useCanvasCart, type CanvasCart } from "./useCanvasCart";
import { useCanvasConsent } from "./useCanvasConsent";
import type { SiteDraftParts } from "../site-view/useSiteDraftParts";
import type { BuilderSelection } from "../../lib/site-builder/selection";
import { cartOf } from "../../lib/site-view/cart-look";

interface Options {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  /** На канве свой черновик (не «Сейчас на сайте» и не снимок). */
  ownDraft: boolean;
  selection: Pick<BuilderSelection, "screen" | "panel" | "sub">;
  parts: SiteDraftParts | null;
  /** Ключ окна канвы. */
  loadKey: string;
}

export interface CanvasOverlays {
  cartActive: boolean;
  cart: CanvasCart;
  /** Окно канвы загрузилось (из `onLoad` iframe). */
  onLoad: () => void;
}

export function useCanvasOverlays({ iframeRef, ownDraft, selection, parts, loadKey }: Options): CanvasOverlays {
  const cartActive = ownDraft && (selection.screen === "cart" || selection.panel === "cart");
  const cart = useCanvasCart({ iframeRef, active: cartActive, afterAdd: parts ? cartOf(parts.theme.theme).afterAdd : null, loadKey });
  const consent = useCanvasConsent({ iframeRef, active: ownDraft && selection.panel === "counters", parts, sub: selection.sub, loadKey });
  const { onLoad: cartLoad } = cart;
  const { onLoad: consentLoad } = consent;
  const onLoad = useCallback(() => {
    cartLoad();
    consentLoad();
  }, [cartLoad, consentLoad]);
  return { cartActive, cart, onLoad };
}
